import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, Info } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Button } from '../../../tailwind/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../tailwind/tooltip';
import { cn } from '../../../../lib/utils';
import { formatWalletCurrency, isLowBalance } from '../../../../utils/wallet/walletUtils';
import { LOW_BALANCE_THRESHOLD } from '../../../../utils/wallet/walletConstants';
import { hasWhatsApp } from '../../../../utils/capabilitiesHelper';
import { waBtn, waSoft } from '../../../../pages/whatsapp/utils/whatsappUiTokens';
import { useWalletHydration } from './hooks/useWalletHydration';

export function WalletBalancePill() {
  useWalletHydration();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const balance = useSelector((state) => state.wallet?.balance ?? 0);
  const warningThreshold = useSelector((state) => state.wallet?.warningThreshold ?? LOW_BALANCE_THRESHOLD);
  const capabilities = useSelector((state) => state.capabilities.capabilities);
  const showWallet = hasWhatsApp(capabilities);

  const lowBalance = isLowBalance(balance, warningThreshold);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!dropdownRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  if (!showWallet) return null;

  const pillClasses = lowBalance
    ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100 dark:bg-red-950/40 dark:text-red-400 dark:border-red-900 dark:hover:bg-red-950/60'
    : cn(
        waSoft.bg,
        waSoft.text,
        'border border-[hsl(158,48%,36%)]/25 dark:border-[hsl(158,48%,36%)]/30',
        'hover:bg-[hsl(158,48%,36%)]/15 dark:hover:bg-[hsl(158,48%,36%)]/25'
      );

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors',
          pillClasses
        )}
        aria-label="WhatsApp wallet balance"
      >
        <Wallet className={cn('h-4 w-4', lowBalance ? 'text-red-600 dark:text-red-400' : waSoft.text)} />
        <span>{formatWalletCurrency(balance)}</span>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn('inline-flex', lowBalance ? 'text-red-400' : 'opacity-70')}
                onClick={(e) => e.stopPropagation()}
              >
                <Info className="h-3.5 w-3.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="bg-gray-900 text-white border-gray-800">
              WhatsApp messaging credits
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg z-30 overflow-hidden animate-fade-in">
          <div className="p-5">
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Current Balance
            </p>
            <p
              className={cn(
                'mt-1 text-3xl font-bold',
                lowBalance ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
              )}
            >
              {formatWalletCurrency(balance)}
            </p>
          </div>
          <div className="border-t border-gray-100 dark:border-slate-800 p-4 space-y-2">
            <Button
              className={cn(waBtn.primary, 'w-full h-10')}
              onClick={() => {
                setOpen(false);
                navigate('/whatsapp/billing?tab=meta-wallet&action=recharge');
              }}
            >
              Add Money
            </Button>
            <Button
              variant="ghost"
              className={cn(waBtn.ghost, 'w-full')}
              onClick={() => {
                setOpen(false);
                navigate('/whatsapp/billing?tab=meta-wallet');
              }}
            >
              View Wallet
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
